import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { companyApi } from '@/api';
import { Alert, Button, Icon, Modal, Textarea, useToast } from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { cn } from '@/utils/cn';

import { REPLY_MAX, formatSlot, validateReply } from './reschedule';
import { ScheduleDialog } from './ScheduleDialog';

/** Everything that shows a round or its application, refreshed after an answer. */
function useInvalidateInterviews() {
  const queryClient = useQueryClient();
  return () => {
    for (const key of [
      'company-interviews',
      'interview-counts',
      'company-application',
      'company-applications',
      'company-application-counts',
    ]) {
      void queryClient.invalidateQueries({ queryKey: [key] });
    }
  };
}

/** The dialog for whichever answer `useRescheduleAnswer` opened. */
export function RescheduleAnswerDialogs({ state, onChange }) {
  if (!state) return null;
  const close = () => onChange(null);
  if (state.mode === 'pick') {
    return (
      <ScheduleDialog
        open
        acceptRequest
        interview={state.interview}
        initialScheduledAt={state.slot ?? null}
        onClose={close}
      />
    );
  }
  if (state.mode === 'decline') {
    return <DeclineRequestDialog interview={state.interview} onClose={close} />;
  }
  return (
    <AcceptRequestDialog
      interview={state.interview}
      initialSlot={state.slot}
      onClose={close}
      onPickAnother={() => onChange({ mode: 'pick', interview: state.interview })}
    />
  );
}

const OTHER = 'other';

/** Pick one of the candidate's times (one step), or another time (the full form). */
export function AcceptRequestDialog({ interview, initialSlot = null, onClose, onPickAnother }) {
  const toast = useToast();
  const invalidate = useInvalidateInterviews();
  const slots = interview.reschedule_request?.proposed_slots ?? [];
  const [choice, setChoice] = useState(initialSlot ?? slots[0] ?? OTHER);
  const name = interview.candidate?.full_name ?? 'The candidate';

  const accept = useMutation({
    mutationFn: (slot) => companyApi.acceptRescheduleRequest(interview.id, { slot }),
    onSuccess: () => {
      toast.success('Interview moved. The candidate has been told their request was accepted.');
      invalidate();
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not accept the request')),
  });

  return (
    <Modal
      open
      title="Accept the request"
      description={`${name} proposed ${slots.length === 1 ? 'this time' : 'these times'}. The interview moves in one step and they are told.`}
      onClose={onClose}
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (choice === OTHER) onPickAnother();
          else accept.mutate(choice);
        }}
      >
        <fieldset className="space-y-2">
          <legend className="sr-only">New time</legend>
          {[...slots, OTHER].map((value) => (
            <label
              key={value}
              className={cn(
                'rounded-input flex cursor-pointer items-center gap-3 border p-3 text-sm transition',
                choice === value
                  ? 'border-brand-300 bg-brand-50 text-ink'
                  : 'border-line text-ink-muted hover:border-brand-200',
              )}
            >
              <input
                type="radio"
                name="reschedule-slot"
                value={value}
                checked={choice === value}
                onChange={() => setChoice(value)}
                className="text-brand-600 focus:ring-brand-500 size-4"
              />
              {value === OTHER ? (
                <span>
                  <span className="text-ink font-medium">Pick another time</span>
                  <span className="block text-xs">Opens the reschedule form.</span>
                </span>
              ) : (
                <span className="text-ink font-medium tabular-nums">{formatSlot(value)}</span>
              )}
            </label>
          ))}
        </fieldset>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Back
          </Button>
          <Button type="submit" variant="success" isLoading={accept.isPending}>
            <Icon name="check" size={14} />
            {choice === OTHER ? 'Choose a time' : 'Accept and move'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Declining cancels the round; the reply is the reason the candidate reads. */
export function DeclineRequestDialog({ interview, onClose }) {
  const toast = useToast();
  const invalidate = useInvalidateInterviews();
  const [reply, setReply] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const error = submitted ? validateReply(reply) : null;

  const decline = useMutation({
    mutationFn: (text) => companyApi.declineRescheduleRequest(interview.id, text),
    onSuccess: () => {
      toast.success('Request declined and the interview cancelled. The candidate has been told.');
      invalidate();
      onClose();
    },
    onError: (failure) => toast.error(errorMessage(failure, 'Could not decline the request')),
  });

  return (
    <Modal open title="Decline the request" onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(true);
          if (!validateReply(reply)) decline.mutate(reply.trim());
        }}
        noValidate
      >
        <Alert tone="warning" title="Declining cancels this interview.">
          The candidate will be told, with your reply below.
        </Alert>
        <Textarea
          label="Reply to the candidate"
          required
          rows={3}
          maxLength={REPLY_MAX}
          placeholder="We can only run this panel on the original date, sorry."
          value={reply}
          error={error}
          onChange={(event) => setReply(event.target.value)}
        />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Back
          </Button>
          <Button type="submit" variant="danger" isLoading={decline.isPending}>
            Decline and cancel interview
          </Button>
        </div>
      </form>
    </Modal>
  );
}
